// 261023 E3-P3b2 — nút «Chấm lại bằng AI» MỘT tập. Dùng ở hàng Sổ duyệt và thẻ giám khảo đầu tập ⑤ (cùng `rejudgeActionOf`).
// Bấm ⇒ POST `rejudge-c2` với `modifiedAt` bản đang xem (CAS) ⇒ nhận job nền ⇒ `refreshAll()` để workbench thấy job và tự poll; xong/lỗi do vỏ báo (`jobTracking`).
// Lỗi lúc bấm (409 SCRIPT_CHANGED / WRITER_UNKNOWN / cổng khung xương-dàn tập…) ⇒ hiện chữ của máy chủ, KHÔNG tự thử lại.
import React, { useRef, useState } from 'react';
import { Tooltip, message } from 'antd';
import Button from './WbButton';
import { adaptationApi } from '../../../services/api/adaptation';
import type { EpisodeScriptRecord } from '../../../types/adaptation';
import { REJUDGE_LABEL, REJUDGE_RUNNING_TEXT, RejudgeAction, rejudgeFailureToast, rejudgeTipHost } from './rejudgeView';

interface Props {
  storyId: string;
  episode: number;
  script: Pick<EpisodeScriptRecord, 'modifiedAt'>;
  action: RejudgeAction;
  /** `refreshAll` của vỏ: tải lại workbench + kịch bản. */
  refreshAll: () => Promise<unknown>;
  size?: 'small' | 'middle';
  /** 3k-a lượt 2 (F5): trạng thái bấm DÙNG CHUNG với mục «⋯ → Chấm lại bằng AI» (cùng `inFlight`/`starting`) ⇒ đang chấm thì cả hai cùng khoá. Vắng ⇒ nút tự giữ (Sổ duyệt). */
  controller?: { starting: boolean; run: () => Promise<void> };
}

/** 3k-a: logic bấm «Chấm lại bằng AI» tách thành hook để cả nút trong «Chi tiết kỹ thuật ▸» lẫn mục «⋯» dùng CHUNG một đường (cùng CAS, cùng chặn bấm đúp). Hành vi nút không đổi. */
export const useRejudgeRun = ({ storyId, episode, script, action, refreshAll }: Pick<Props, 'storyId' | 'episode' | 'script' | 'action' | 'refreshAll'>): { starting: boolean; run: () => Promise<void> } => {
  const [starting, setStarting] = useState(false);
  const inFlight = useRef(false);   // chặn bấm đúp trước khi state kịp render
  const run = async () => {
    if (inFlight.current || action.disabled || action.running || !script.modifiedAt) return;
    inFlight.current = true;
    setStarting(true);
    try {
      await adaptationApi.rejudgeC2(storyId, episode, script.modifiedAt);
    } catch (e) {
      message.error(rejudgeFailureToast(e));
    } finally {
      try { await refreshAll(); } catch { /* vỏ tự hiện lỗi tải */ }
      inFlight.current = false;
      setStarting(false);
    }
  };
  return { starting, run };
};

const RejudgeButton: React.FC<Props> = ({ storyId, episode, script, action, refreshAll, size = 'small', controller }) => {
  const own = useRejudgeRun({ storyId, episode, script, action, refreshAll });
  const { starting, run } = controller ?? own;
  if (!action.show) return null;

  if (action.running || starting) {
    return <Button size={size} disabled loading data-testid="rejudge-running">{REJUDGE_RUNNING_TEXT}</Button>;
  }
  const onClick = run;

  const button = (
    <Button
      size={size} type={action.primary ? 'primary' : 'default'} ghost={action.primary} disabled={action.disabled}
      onClick={() => { void onClick(); }} data-testid="rejudge-btn"
    >{REJUDGE_LABEL}</Button>
  );
  // Nút `disabled` không nhận chuột ⇒ Tooltip phải bọc một <span> (E3-P3c2a, lỗi smoke 3b2) để lý do khoá hiện ra.
  return (
    <Tooltip title={action.tip}>
      {rejudgeTipHost(action) === 'span' ? <span style={{ display: 'inline-block' }}>{button}</span> : button}
    </Tooltip>
  );
};

export default RejudgeButton;
